<template>
  <v-row
    class="ma-0 pa-0 inventory-map"
    align="center"
    justify="center"
    :style="{ '--inventory-map-height': mapHeight }"
  >
    <v-col cols="12" class="inventory-map__wrap ma-0 pa-0">
      <div ref="mapContainer" class="inventory-map__map"></div>
      <v-btn
        v-if="selectedLocationKey"
        class="inventory-map__clear-selection"
        small
        color="white"
        elevation="2"
        @click.stop="clearMarkerSelection"
      >
        <v-icon left small>mdi-close</v-icon>
        Clear Marker
      </v-btn>
      <v-row
        v-if="loading"
        class="inventory-map__loader ma-0 pa-0"
        align="center"
        justify="center"
      >
        <v-progress-circular color="black" size="64" indeterminate></v-progress-circular>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
const CANADA_MAP_URL = "/data/GeoData/canada_provinces_territories.geojson";
const CMA_CA_BOUNDARIES_URL = "/data/GeoData/cma_ca_boundaries.geojson";
const LEAFLET_SCRIPT_URL = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
const LEAFLET_STYLE_URL = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const STREET_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const STREET_TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const CANADA_BOUNDS = [
  [40, -142],
  [84, -52],
];

export const PRODUCT_STYLES = {
  "Airport Interior Digital": { color: "#004E59", shape: "pin" },
  "Digital Urban Pathway": { color: "#8F1515", shape: "diamond" },
  "Ferries Digital": { color: "#0B4F6C", shape: "triangle" },
  "ICE District Digital": { color: "#4B0A78", shape: "square" },
  "Office Digital": { color: "#00665B", shape: "square" },
  "Outdoor Video Board": { color: "#9A4D00", shape: "circle" },
  "Posters Horizontal Digital": { color: "#3F6B22", shape: "triangle" },
  "Street Level Digital": { color: "#7E1F25", shape: "pin" },
  "Superboards Digital": { color: "#24116B", shape: "diamond" },
  "Transit Station Digital": { color: "#075D7E", shape: "triangle" },
  "Transit Station Network": { color: "#5A189A", shape: "square" },
};
const HIGHLIGHTED_MARKER_STYLE = { color: "#FFE600", shape: "circle" };
const CMA_CA_REGION_COLORS = [
  "#A8DADC",
  "#B8E0D2",
  "#CDB4DB",
  "#F1C0E8",
  "#F7D9A6",
  "#BDE0FE",
  "#D9ED92",
  "#F6B8A8",
];

let leafletLoader = null;

export default {
  name: "SweetSpotInventoryMap",
  props: {
    locations: {
      type: Array,
      default: () => [],
    },
    mapHeight: {
      type: String,
      default: "clamp(300px, 44vh, 520px)",
    },
  },
  data: () => ({
    canadaData: null,
    cmaCaData: null,
    loading: true,
    map: null,
    cmaLayer: null,
    canadaMaskLayer: null,
    markerLayer: null,
    markerByLocationKey: {},
    selectedLocationKey: null,
    vectorRenderer: null,
  }),
  computed: {
    validLocations: function () {
      return (this.locations || []).filter((location) => {
        return this.getLocationCoordinates(location) != null;
      });
    },
  },
  watch: {
    validLocations: {
      handler() {
        this.invalidateMapSize();
        this.renderMarkers();
        this.fitMapToContent();
      },
      deep: true,
    },
  },
  async mounted() {
    this.loading = true;
    await Promise.all([this.loadLeaflet(), this.loadMapData()]);
    this.createMap();
    this.renderCanadaMask();
    this.renderCmaLayer();
    this.renderMarkers();
    this.$nextTick(() => {
      this.invalidateMapSize();
      this.fitMapToContent();
    });
    this.loading = false;
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    loadLeaflet() {
      if (window.L) {
        return Promise.resolve(window.L);
      }

      if (leafletLoader) {
        return leafletLoader;
      }

      leafletLoader = new Promise((resolve, reject) => {
        if (!document.querySelector(`link[href="${LEAFLET_STYLE_URL}"]`)) {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = LEAFLET_STYLE_URL;
          document.head.appendChild(link);
        }

        const script = document.createElement("script");
        script.src = LEAFLET_SCRIPT_URL;
        script.onload = () => resolve(window.L);
        script.onerror = reject;
        document.head.appendChild(script);
      });

      return leafletLoader;
    },
    async loadMapData() {
      const [canadaData, cmaCaData] = await Promise.all([
        fetch(CANADA_MAP_URL).then((res) => res.json()),
        fetch(CMA_CA_BOUNDARIES_URL).then((res) => res.json()),
      ]);
      this.canadaData = canadaData;
      this.cmaCaData = cmaCaData;
    },
    createMap() {
      if (this.map || !this.$refs.mapContainer || !window.L) {
        return;
      }

      this.map = window.L.map(this.$refs.mapContainer, {
        attributionControl: true,
        maxBounds: CANADA_BOUNDS,
        maxBoundsViscosity: 1,
        minZoom: 3,
        preferCanvas: true,
        scrollWheelZoom: true,
        zoomControl: true,
      });
      this.vectorRenderer = window.L.canvas({ padding: 0.5 });
      this.map.createPane("canadaMaskPane");
      this.map.getPane("canadaMaskPane").style.zIndex = 350;
      this.map.createPane("cmaPane");
      this.map.getPane("cmaPane").style.zIndex = 400;

      window.L.tileLayer(STREET_TILE_URL, {
        attribution: STREET_TILE_ATTRIBUTION,
        bounds: CANADA_BOUNDS,
        maxZoom: 19,
        noWrap: true,
        referrerPolicy: "strict-origin-when-cross-origin",
      }).addTo(this.map);

      this.canadaMaskLayer = window.L.featureGroup().addTo(this.map);
      this.cmaLayer = window.L.featureGroup().addTo(this.map);
      this.markerLayer = window.L.featureGroup().addTo(this.map);
    },
    invalidateMapSize() {
      if (!this.map) {
        return;
      }

      this.$nextTick(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      });
    },
    renderCanadaMask() {
      if (!this.map || !this.canadaMaskLayer || !this.canadaData || !window.L) {
        return;
      }

      this.canadaMaskLayer.clearLayers();
      window.L.geoJSON(this.canadaData, {
        interactive: false,
        pane: "canadaMaskPane",
        renderer: this.vectorRenderer,
        smoothFactor: 0,
        style: {
          color: "#68736E",
          fillColor: "#F7F8F6",
          fillOpacity: 0.28,
          opacity: 0.65,
          weight: 0.8,
        },
      }).addTo(this.canadaMaskLayer);
    },
    renderCmaLayer() {
      if (!this.map || !this.cmaLayer || !this.cmaCaData || !window.L) {
        return;
      }

      this.cmaLayer.clearLayers();
      window.L.geoJSON(this.cmaCaData, {
        pane: "cmaPane",
        renderer: this.vectorRenderer,
        smoothFactor: 0,
        style: (feature) => {
          const featureIndex = this.cmaCaData.features.indexOf(feature);
          return {
            className: "inventory-map__cma-outline",
            color: "#1F2623",
            fillColor: CMA_CA_REGION_COLORS[featureIndex % CMA_CA_REGION_COLORS.length],
            fillOpacity: 0.26,
            lineCap: "round",
            lineJoin: "round",
            opacity: 0.82,
            weight: 1.35,
          };
        },
        onEachFeature: (feature, layer) => {
          const name = feature.properties?.CMANAME || "CMA/CA Region";
          layer.bindTooltip(name, {
            direction: "center",
            opacity: 0.78,
            sticky: true,
          });
        },
      }).addTo(this.cmaLayer);
    },
    renderMarkers() {
      if (!this.map || !this.markerLayer || !window.L) {
        return;
      }

      this.markerLayer.clearLayers();
      this.markerByLocationKey = {};
      this.validLocations.forEach((location) => {
        const coordinates = this.getLocationCoordinates(location);
        const style = PRODUCT_STYLES[location.product] || { color: "#1F2933", shape: "circle" };
        const locationKey = this.getLocationKey(location);
        const isHighlighted = locationKey === this.selectedLocationKey;
        const marker = window.L.marker([coordinates[1], coordinates[0]], {
          icon: this.createMarkerIcon(style, isHighlighted),
          title: location.panel || location.product || "Inventory Location",
          zIndexOffset: isHighlighted ? 1000 : 0,
        });

        marker.bindPopup(this.getLocationPopup(location));
        marker.addTo(this.markerLayer);
        this.markerByLocationKey[locationKey] = marker;
      });
    },
    createMarkerIcon(style, isHighlighted = false) {
      const markerStyle = isHighlighted ? HIGHLIGHTED_MARKER_STYLE : style;
      const highlightedClass = isHighlighted ? " inventory-map__marker--highlighted" : "";

      return window.L.divIcon({
        className: "inventory-map__marker-shell",
        html: `<span class="inventory-map__marker inventory-map__marker--${markerStyle.shape}${highlightedClass}" style="background:${markerStyle.color}; color:${markerStyle.color};"></span>`,
        iconAnchor: isHighlighted ? [11, 11] : [8, 8],
        iconSize: isHighlighted ? [22, 22] : [16, 16],
        popupAnchor: [0, isHighlighted ? -11 : -8],
      });
    },
    fitMapToContent() {
      if (!this.map || !window.L) {
        return;
      }

      const bounds = window.L.latLngBounds();
      this.validLocations.forEach((location) => {
        const coordinates = this.getLocationCoordinates(location);
        bounds.extend([coordinates[1], coordinates[0]]);
      });

      if (bounds.isValid()) {
        this.map.fitBounds(bounds.pad(0.25), { maxZoom: 10 });
        return;
      }

      if (this.cmaLayer && this.cmaLayer.getLayers().length > 0) {
        this.map.fitBounds(this.cmaLayer.getBounds().pad(0.05));
        return;
      }

      this.map.fitBounds(CANADA_BOUNDS);
    },
    focusLocation(location) {
      if (!this.map) {
        return;
      }

      const coordinates = this.getLocationCoordinates(location);
      if (!coordinates) {
        return;
      }

      this.selectedLocationKey = this.getLocationKey(location);
      this.renderMarkers();

      this.map.setView([coordinates[1], coordinates[0]], Math.max(this.map.getZoom(), 15), {
        animate: true,
      });

      const selectedMarker = this.markerByLocationKey[this.selectedLocationKey];
      if (selectedMarker) {
        selectedMarker.openPopup();
      }
    },
    clearMarkerSelection() {
      this.selectedLocationKey = null;
      this.renderMarkers();

      if (this.map) {
        this.map.closePopup();
      }
    },
    getLocationKey(location) {
      if (!location) {
        return "";
      }

      return [
        location.plan_id,
        location.panel,
        location.product,
        location.latitude,
        location.longitude,
      ].join("|");
    },
    getLocationPopup(location) {
      return [
        `<strong>${this.escapeHtml(location.panel || "Inventory Location")}</strong>`,
        this.escapeHtml(location.product || ""),
        this.escapeHtml(`${location.market || ""}${location.province ? ", " + location.province : ""}`),
        this.escapeHtml(location.address || ""),
        `Weekly Impressions: ${this.formatNumber(location.impressions_weekly)}`,
      ].filter(Boolean).join("<br/>");
    },
    formatNumber(value) {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue.toLocaleString("en-CA") : "0";
    },
    escapeHtml(value) {
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },
    isCanadianCoordinatePair(longitude, latitude) {
      return (
        Number.isFinite(longitude) &&
        Number.isFinite(latitude) &&
        longitude >= -142 &&
        longitude <= -52 &&
        latitude >= 40 &&
        latitude <= 84
      );
    },
    getLocationCoordinates(location) {
      const latitude = Number(location.latitude);
      const longitude = Number(location.longitude);

      if (this.isCanadianCoordinatePair(longitude, latitude)) {
        return [longitude, latitude];
      }

      if (this.isCanadianCoordinatePair(latitude, longitude)) {
        return [latitude, longitude];
      }

      return null;
    },
  },
};
</script>

<style scoped>
.inventory-map {
  min-height: var(--inventory-map-height);
}

.inventory-map__wrap {
  height: var(--inventory-map-height);
  isolation: isolate;
  position: relative;
  width: 100%;
  z-index: 0;
}

.inventory-map__map {
  background: #d8dad8;
  height: 100%;
  position: relative;
  width: 100%;
  z-index: 0;
}

.inventory-map__clear-selection {
  color: #111827 !important;
  font-size: 0.72rem;
  font-weight: 600;
  position: absolute;
  right: 12px;
  text-transform: none;
  top: 12px;
  z-index: 650;
}

.inventory-map__loader {
  background: rgba(255, 255, 255, 0.72);
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 500;
}

::v-deep .inventory-map__marker-shell {
  background: transparent;
  border: 0;
}

::v-deep .inventory-map__marker {
  border: 2px solid #111827;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.82), 0 2px 6px rgba(0, 0, 0, 0.45);
  display: block;
  height: 15px;
  width: 15px;
}

::v-deep .inventory-map__marker--highlighted {
  border: 3px solid #111827;
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.95), 0 0 0 8px rgba(255, 230, 0, 0.45), 0 4px 10px rgba(0, 0, 0, 0.48);
  height: 22px;
  width: 22px;
}

::v-deep .inventory-map__marker--circle {
  border-radius: 50%;
}

::v-deep .inventory-map__marker--diamond {
  transform: rotate(45deg);
}

::v-deep .inventory-map__marker--pin {
  border-radius: 50% 50% 50% 0;
  transform: rotate(-45deg);
}

::v-deep .inventory-map__marker--square {
  border-radius: 2px;
}

::v-deep .inventory-map__marker--triangle {
  background: transparent !important;
  border-bottom: 16px solid currentColor;
  border-left: 8px solid transparent !important;
  border-right: 8px solid transparent !important;
  border-top: 0 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.45);
  height: 0;
  width: 0;
}

::v-deep .inventory-map__marker--triangle.inventory-map__marker--highlighted {
  border-bottom-width: 22px;
  border-left-width: 11px !important;
  border-right-width: 11px !important;
}

::v-deep .inventory-map__cma-outline {
  stroke-linecap: round;
  stroke-linejoin: round;
}
</style>
